// Purchase orders (Session ASY): supplier orders, upstream of van stock. A filterable list with a "New
// purchase order" modal; a row opens a detail modal where the office marks it ordered, receives some or
// all of its items (partial receipt supported), cancels it, or deletes it. Office/admin.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  api,
  ApiError,
  type PurchaseOrderCreateInput,
  type PurchaseOrderItemInput,
  type PurchaseOrderListFilters,
  type PurchaseOrderListItem,
  type PurchaseOrderStatus,
  type ScheduleEngineer,
} from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { Button } from "../../components/Button";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { ListToolbar } from "../../components/ListToolbar";
import { SortableHeader } from "../../components/SortableHeader";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { SupplierOrderDraftPanel } from "./purchaseOrders/SupplierOrderDraftPanel";

const STATUS_FILTERS: Array<{ value: PurchaseOrderStatus | ""; label: string }> = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "ordered", label: "Ordered" },
  { value: "partially_received", label: "Partially received" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

// The pill keeps the server's own word (statusLabel) and takes the registry's tone.
const PO_SORT_ACCESSORS: SortAccessors<PurchaseOrderListItem> = {
  reference: (r) => r.reference,
  supplier: (r) => r.supplier,
  engineer: (r) => r.engineerName ?? r.projectRef ?? null,
  items: (r) => r.itemCount,
  status: (r) => r.statusLabel,
  raised: (r) => r.createdAt,
};
const PO_SORT_OPTIONS = [
  { key: "reference", label: "Reference" },
  { key: "supplier", label: "Supplier" },
  { key: "engineer", label: "Van or project" },
  { key: "items", label: "Items" },
  { key: "status", label: "Status" },
  { key: "raised", label: "Raised" },
];

function money(pence: number | null): string {
  if (pence == null) return "-";
  return `£${(pence / 100).toFixed(2)}`;
}

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function PurchaseOrdersPage() {
  const [rows, setRows] = useState<PurchaseOrderListItem[] | null>(null);
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState<PurchaseOrderStatus | "">("");
  const [supplier, setSupplier] = useState("");
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const sort = useSortableColumn("purchase-orders");
  // Below md the list renders as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(() => {
    setLoadError(false);
    const filters: PurchaseOrderListFilters = {};
    if (status) filters.status = status;
    if (supplier.trim()) filters.supplier = supplier.trim();
    api.purchaseOrders
      .list(filters)
      .then(setRows)
      .catch(() => setLoadError(true));
  }, [status, supplier]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.scheduling.engineers().then(setEngineers).catch(() => setEngineers([]));
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        className="mb-4"
        title="Purchase orders"
        subtitle="Orders raised with suppliers for parts, and what has arrived into each engineer's van."
        actions={
          <Button onClick={() => setAdding(true)} icon={<Plus aria-hidden size={15} />}>
            New purchase order
          </Button>
        }
      />

      {/* The supplier term goes to the server (it filters the list there), exactly as the old box did:
          the toolbar's search box IS that filter, so nothing is searched over one page only. */}
      <ListToolbar
        className="mb-4"
        search={supplier}
        onSearchChange={setSupplier}
        searchPlaceholder="Filter by supplier"
        searchLabel="Filter by supplier"
        sort={{ state: sort, options: PO_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
        onClear={() => {
          setSupplier("");
          setStatus("");
        }}
        filters={STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            onClick={() => setStatus(f.value)}
            className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              status === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {f.label}
          </button>
        ))}
      />

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load purchase orders. Please try again.
        </p>
      ) : rows === null ? (
        <Loader />
      ) : rows.length === 0 ? (
        <EmptyState role={status || supplier ? "status" : undefined} message={`No purchase orders on record${status || supplier ? " matching this filter" : ""}.`} />
      ) : mobileCards ? (
        /* Below md the six columns cannot share 375px. Each order is a card: the reference never
           truncates, the engineer's van and the raised date the table hid come back, and a tap
           opens the same detail modal the row does. */
        <MobileCardList label="Purchase orders">
          {sortRows(rows, PO_SORT_ACCESSORS, sort).map((r) => (
            <MobileListCard
              key={r.id}
              onClick={() => setOpenId(r.id)}
              primary={r.reference}
              secondary={
                <>
                  {r.supplier}
                  {r.engineerName && <span className="block text-xs opacity-70">Van: {r.engineerName}</span>}
                  {r.projectRef && <span className="block text-xs opacity-70">Project: {r.projectRef}</span>}
                </>
              }
              meta={
                <>
                  <span className="block">{r.itemCount === 1 ? "1 item" : `${r.itemCount} items`}</span>
                  <span className="block">
                    {formatDate(r.createdAt)} by {r.raisedByName}
                  </span>
                </>
              }
              right={<StatusPill word={r.statusLabel} tone={statusEntry("purchaseOrder", r.status).tone} />}
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "16%" }} />
              <col style={{ width: "22%" }} />
              <col className="max-md:hidden" style={{ width: "18%" }} />
              <col style={{ width: "10%" }} />
              <col className="cell-actions" style={{ width: "16%" }} />
              <col className="max-md:hidden cell-actions" style={{ width: "18%" }} />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <SortableHeader label="Reference" colKey="reference" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Supplier" colKey="supplier" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Van or project" colKey="engineer" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Items" colKey="items" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} className="cell-actions" />
                <SortableHeader label="Raised" colKey="raised" sort={sort} onToggle={sort.toggle} className="max-md:hidden cell-actions" />
              </tr>
            </thead>
            <tbody>
              {sortRows(rows, PO_SORT_ACCESSORS, sort).map((r) => (
                <tr key={r.id} onClick={() => setOpenId(r.id)} className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface">
                  <td className="px-3 py-2 font-semibold text-ele-strong">{r.reference}</td>
                  <td className="px-3 py-2 text-ele-body">{r.supplier}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{r.engineerName ?? (r.projectRef ? `Project ${r.projectRef}` : "-")}</td>
                  <td className="px-3 py-2 text-ele-body">{r.itemCount}</td>
                  <td className="cell-actions px-3 py-2">
                    <StatusPill word={r.statusLabel} tone={statusEntry("purchaseOrder", r.status).tone} />
                  </td>
                  <td className="max-md:hidden cell-actions px-3 py-2 text-xs text-ele-body">
                    {formatDate(r.createdAt)} by {r.raisedByName}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <CreatePurchaseOrderModal
          engineers={engineers}
          onClose={() => setAdding(false)}
          onCreated={(id) => {
            setAdding(false);
            load();
            setOpenId(id);
          }}
        />
      )}

      {openId && (
        <PurchaseOrderDetailModal
          id={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
          onDeleted={() => {
            setOpenId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ---- Create ----------------------------------------------------------------------------------------------

function emptyItem(): PurchaseOrderItemInput {
  return { partCode: "", description: "", quantity: 1, unitCost: null };
}

function CreatePurchaseOrderModal({ engineers, onClose, onCreated }: { engineers: ScheduleEngineer[]; onClose: () => void; onCreated: (id: string) => void }) {
  const [supplier, setSupplier] = useState("");
  const [engineerId, setEngineerId] = useState(engineers[0]?.id ?? "");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState<PurchaseOrderItemInput[]>([emptyItem()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!engineerId && engineers[0]) setEngineerId(engineers[0].id);
  }, [engineers, engineerId]);

  const setItem = (i: number, patch: Partial<PurchaseOrderItemInput>) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const removeItem = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!supplier.trim()) return setErr("Enter the supplier's name.");
    if (!engineerId) return setErr("Say which engineer's van these parts are for.");
    const cleaned = items.filter((it) => it.description.trim());
    if (cleaned.length === 0) return setErr("Add at least one item.");
    for (const it of cleaned) {
      if (!it.quantity || it.quantity < 1) return setErr(`Give a quantity for "${it.description}".`);
    }
    setBusy(true);
    setErr(null);
    const body: PurchaseOrderCreateInput = {
      supplier: supplier.trim(),
      engineerId,
      notes: notes.trim() || null,
      items: cleaned.map((it) => ({ partCode: it.partCode?.trim() || null, description: it.description.trim(), quantity: it.quantity, unitCost: it.unitCost ?? null })),
    };
    try {
      const created = await api.purchaseOrders.create(body);
      onCreated(created.id);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the purchase order. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="New purchase order" onClose={() => !busy && onClose()} width="max-w-3xl" margin="my-8" panelClassName="p-6">
      <h2 className="mb-3 font-heading text-xl tracking-wide text-ele-strong">New purchase order</h2>
      <form onSubmit={save} className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <label>
            <span className={labelCls}>Supplier</span>
            <input aria-label="Supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. Acme Lift Parts" className={`${inputCls} w-full`} />
          </label>
          <label>
            <span className={labelCls}>Engineer's van</span>
            <select aria-label="Engineer's van" value={engineerId} onChange={(e) => setEngineerId(e.target.value)} className={`${inputCls} w-full`}>
              {engineers.length === 0 && <option value="">No engineers on file</option>}
              {engineers.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <span className={labelCls}>Items</span>
          <div className="grid gap-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_7rem_5rem_5rem_2rem] items-center gap-2">
                <input
                  aria-label={`Item ${i + 1} description`}
                  value={it.description}
                  onChange={(e) => setItem(i, { description: e.target.value })}
                  placeholder="Description"
                  className={inputCls}
                />
                <input aria-label={`Item ${i + 1} part code`} value={it.partCode ?? ""} onChange={(e) => setItem(i, { partCode: e.target.value })} placeholder="Part code" className={inputCls} />
                <input
                  aria-label={`Item ${i + 1} quantity`}
                  type="number"
                  min={1}
                  value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: Number(e.target.value) })}
                  className={inputCls}
                />
                <input
                  aria-label={`Item ${i + 1} unit cost`}
                  type="number"
                  min={0}
                  step="0.01"
                  value={it.unitCost ?? ""}
                  onChange={(e) => setItem(i, { unitCost: e.target.value === "" ? null : Number(e.target.value) })}
                  placeholder="Cost £"
                  className={inputCls}
                />
                <button type="button" aria-label={`Remove item ${i + 1}`} onClick={() => removeItem(i)} className="text-ele-body hover:text-ele-danger">
                  <Trash2 aria-hidden size={16} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setItems((prev) => [...prev, emptyItem()])} className="mt-2 font-menu text-xs tracking-wide text-ele-green hover:underline">
            + Add another item
          </button>
        </div>

        <label>
          <span className={labelCls}>Notes (optional)</span>
          <textarea aria-label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${inputCls} w-full`} />
        </label>

        {err && (
          <p role="alert" className="rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
            {err}
          </p>
        )}
        <div className={`mt-2 gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Save as draft"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

// ---- Detail / actions -------------------------------------------------------------------------------------

function PurchaseOrderDetailModal({ id, onClose, onChanged, onDeleted }: { id: string; onClose: () => void; onChanged: () => void; onDeleted: () => void }) {
  const [order, setOrder] = useState<PurchaseOrderListItem | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [receiveNow, setReceiveNow] = useState<Record<string, string>>({});
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const load = useCallback(() => {
    api.purchaseOrders
      .get(id)
      .then(setOrder)
      .catch(() => setErr("Could not load this purchase order."));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function markOrdered() {
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.purchaseOrders.markOrdered(id);
      setOrder(updated);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not mark this order as ordered.");
    } finally {
      setBusy(false);
    }
  }

  async function cancelOrder() {
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.purchaseOrders.cancel(id);
      setOrder(updated);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not cancel this order.");
    } finally {
      setBusy(false);
    }
  }

  async function submitReceipt() {
    if (!order) return;
    const lines = order.items
      .map((it) => ({ itemId: it.id, receivedNow: Number(receiveNow[it.id] ?? 0) }))
      .filter((l) => l.receivedNow > 0);
    if (lines.length === 0) return setErr("Enter how many of at least one item arrived.");
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.purchaseOrders.receive(id, lines);
      setOrder(updated);
      setReceiveNow({});
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message || e.code : "Could not record this receipt.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    setBusy(true);
    setErr(null);
    try {
      await api.purchaseOrders.remove(id);
      onDeleted();
    } catch (e) {
      setBusy(false);
      setConfirmingDelete(false);
      setErr(e instanceof ApiError ? e.message || e.code : "Could not delete this purchase order.");
    }
  }

  if (!order) {
    return (
      <ModalShell ariaLabel="Purchase order" onClose={onClose} width="max-w-2xl" margin="my-8" panelClassName="p-6">
        {err ? <p className="text-center text-ele-body">{err}</p> : <Loader shape="panel" />}
      </ModalShell>
    );
  }

  return (
    <ModalShell ariaLabel={`Purchase order ${order.reference}`} onClose={() => !busy && onClose()} width="max-w-2xl" margin="my-8" panelClassName="p-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-heading text-xl tracking-wide text-ele-strong">{order.reference}</h2>
        <StatusPill word={order.statusLabel} tone={statusEntry("purchaseOrder", order.status).tone} />
      </div>
      <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-ele-body">Supplier</dt>
        <dd className="text-ele-strong">{order.supplier}</dd>
        {order.projectRef ? (
          <>
            <dt className="text-ele-body">For project</dt>
            <dd className="text-ele-strong" data-order-project>
              {order.projectRef}
              {order.projectSiteName ? `, ${order.projectSiteName}` : ""} (received against the project, never into a van)
            </dd>
          </>
        ) : (
          <>
            <dt className="text-ele-body">Engineer's van</dt>
            <dd className="text-ele-strong">{order.engineerName ?? "-"}</dd>
          </>
        )}
        {order.supplierQuoteRef && (
          <>
            <dt className="text-ele-body">Supplier's quotation</dt>
            <dd className="text-ele-strong">{order.supplierQuoteRef}</dd>
          </>
        )}
        <dt className="text-ele-body">Raised by</dt>
        <dd className="text-ele-strong">
          {order.raisedByName} · {formatDate(order.createdAt)}
        </dd>
        {order.orderedAt && (
          <>
            <dt className="text-ele-body">Ordered</dt>
            <dd className="text-ele-strong">{formatDate(order.orderedAt)}</dd>
          </>
        )}
        {order.receivedAt && (
          <>
            <dt className="text-ele-body">Received</dt>
            <dd className="text-ele-strong">{formatDate(order.receivedAt)}</dd>
          </>
        )}
        {order.cancelledAt && (
          <>
            <dt className="text-ele-body">Cancelled</dt>
            <dd className="text-ele-strong">{formatDate(order.cancelledAt)}</dd>
          </>
        )}
        {order.notes && (
          <>
            <dt className="text-ele-body">Notes</dt>
            <dd className="text-ele-strong">{order.notes}</dd>
          </>
        )}
        <dt className="text-ele-body">Total cost</dt>
        <dd className="text-ele-strong">{money(order.totalCostPence)}</dd>
      </dl>

      {/* Session AUH (Phase 7): what the supplier chase reads. A record; nothing is sent from here. */}
      <DeliveryPanel order={order} onSaved={(o) => { setOrder(o); onChanged(); }} />

      {/* Session PJ1F: the order as the supplier receives it, and its Gmail draft (never a send). */}
      <SupplierOrderDraftPanel order={order} onDrafted={(at) => { setOrder((o) => (o ? { ...o, supplierDraftedAt: at } : o)); onChanged(); }} />

      <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
        <table className="w-full text-left text-sm">
          <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
            <tr>
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">Ordered</th>
              <th className="px-3 py-2">Received</th>
              {order.canReceive && <th className="px-3 py-2">Receive now</th>}
            </tr>
          </thead>
          <tbody>
            {order.items.map((it) => (
              <tr key={it.id} className="border-t border-ele-hairline">
                <td className="px-3 py-2 text-ele-strong">
                  {it.description}
                  {it.partCode ? <span className="ml-1 text-xs text-ele-body">({it.partCode})</span> : null}
                </td>
                <td className="px-3 py-2 text-ele-body">{it.quantity}</td>
                <td className="px-3 py-2 text-ele-body">{it.receivedQuantity}</td>
                {order.canReceive && (
                  <td className="px-3 py-2">
                    <input
                      aria-label={`Receive now: ${it.description}`}
                      type="number"
                      min={0}
                      max={it.outstanding}
                      disabled={it.outstanding === 0}
                      value={receiveNow[it.id] ?? ""}
                      onChange={(e) => setReceiveNow((prev) => ({ ...prev, [it.id]: e.target.value }))}
                      className={`${inputCls} w-20`}
                      placeholder={it.outstanding === 0 ? "Done" : String(it.outstanding)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {err && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {err}
        </p>
      )}

      <div className={`mt-4 flex-wrap gap-2 ${MODAL_ACTIONS}`}>
        {order.canDelete && (
          <Button
            onClick={() => setConfirmingDelete(true)}
            disabled={busy}
            variant="destructive" className={`mr-auto ${MODAL_ACTION_BTN}`}
          >
            Delete
          </Button>
        )}
        {order.canCancel && (
          <button type="button" onClick={cancelOrder} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`}>
            Cancel order
          </button>
        )}
        <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
          Close
        </button>
        {order.canMarkOrdered && (
          <Button onClick={markOrdered} disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Mark ordered"}
          </Button>
        )}
        {order.canReceive && (
          <Button onClick={submitReceipt} disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Record receipt"}
          </Button>
        )}
      </div>

      <ConfirmDeleteModal
        open={confirmingDelete}
        title="Delete this purchase order?"
        message={`This permanently deletes ${order.reference}. It can only be deleted because nothing has been received against it.`}
        busy={busy}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    </ModalShell>
  );
}

/** When the delivery is expected and where to chase it (Session AUH). Read-only once the order is received or cancelled. */
function DeliveryPanel({ order, onSaved }: { order: PurchaseOrderListItem; onSaved: (o: PurchaseOrderListItem) => void }) {
  const closed = order.status === "received" || order.status === "cancelled";
  const [expectedAt, setExpectedAt] = useState(order.expectedAt ?? "");
  const [email, setEmail] = useState(order.supplierEmail ?? "");
  const [copy, setCopy] = useState(order.supplierCopyEmail ?? "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const changed = expectedAt !== (order.expectedAt ?? "") || email.trim() !== (order.supplierEmail ?? "") || copy.trim() !== (order.supplierCopyEmail ?? "");

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const updated = await api.purchaseOrders.setDelivery(order.id, { expectedAt: expectedAt || null, supplierEmail: email.trim() || null, supplierCopyEmail: copy.trim() || null });
      onSaved(updated);
      setMsg("Saved. The supplier is chased from the day after the expected date.");
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message || e.code : "Could not save the delivery details.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4 rounded-ele border border-ele-hairline p-3 text-sm" aria-label="Delivery and chasing">
      <p className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-blue">Delivery and chasing</p>
      {closed ? (
        <p className="text-ele-body">
          {order.expectedAt ? `Expected ${order.expectedAt}. ` : ""}
          {order.supplierEmail ? `Chased at ${order.supplierEmail}.` : "No supplier email was recorded."}
        </p>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="block text-xs text-ele-body">
              Expected
              <input type="date" aria-label="Expected delivery date" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} className={`${inputCls} mt-1 w-full`} />
            </label>
            <label className="block text-xs text-ele-body">
              Supplier email
              <input type="email" aria-label="Supplier email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1 w-full`} />
            </label>
            <label className="block text-xs text-ele-body">
              Copy on the third chase (optional)
              <input type="email" aria-label="Supplier copy email" value={copy} onChange={(e) => setCopy(e.target.value)} className={`${inputCls} mt-1 w-full`} />
            </label>
          </div>
          <p className="mt-1 text-xs text-ele-body">Once ordered, an overdue delivery is chased on the supplier ladder. The chase is drafted, never sent on its own.</p>
          {changed && (
            <Button disabled={busy} onClick={() => void save()} variant="secondary" size="sm" className="mt-2">
              {busy ? "Saving..." : "Save delivery details"}
            </Button>
          )}
        </>
      )}
      {msg && (
        <p role="status" className="mt-2 text-xs text-ele-body">
          {msg}
        </p>
      )}
    </div>
  );
}
